import React, { ComponentProps } from "react";

import Autocomplete from "veneer/AutocompleteSelectInput";
import Stack from "veneer/Stack";
import TextInput from "veneer/TextInput";
import { Colors } from "veneer/Theme";

import SystemStrategyForm from "cortex/ui/src/fleet/view/strategy/component/SystemStrategyForm";
import { useAppSelector, useFeatureFlag } from "cortex/ui/src/hooks";
import { FormData } from "cortex/ui/src/network/model";
import { getAuthUser } from "cortex/ui/src/redux-store/auth/selectors";
import { isOperator } from "cortex/ui/src/redux-store/users/selectors";
import { OperationScheduleForm } from "cortex/ui/src/ui/operation-schedule";
import { NumberOption, StringOption } from "cortex/ui/src/ui/table/types";
import ToggleSection from "cortex/ui/src/ui/toggle-section";

interface EditNetworkFormProps {
  agentVersionOptions: ReadonlyArray<StringOption>;
  applicationOptions: ReadonlyArray<NumberOption>;
  dayOptions?: ReadonlyArray<NumberOption>;
  errors: {
    networkName?: string;
  };
  hourOptions?: ReadonlyArray<NumberOption>;
  onChange: (nextValue: EditNetworkFormProps["value"]) => void;
  playerVersionOptions: ReadonlyArray<StringOption>;
  systemManaged?: boolean;
  readonly?: boolean;
  value: FormData;
}

export default function EditNetworkForm({
  agentVersionOptions,
  applicationOptions,
  dayOptions = [],
  errors,
  hourOptions = [],
  onChange,
  playerVersionOptions,
  readonly = false,
  systemManaged = false,
  value,
}: EditNetworkFormProps) {
  const isStrategiesEnabled = useFeatureFlag("is-strategies-enabled");
  const user = useAppSelector(getAuthUser);
  const userIsOperator = isOperator(user);
  const selectedDayOption = dayOptions.find((d) => d.value === value.day);
  const selectedHourOption = hourOptions.find((h) => h.value === value.hour);
  const update = (patch: Partial<EditNetworkFormProps["value"]>) => {
    onChange({ ...value, ...patch });
  };

  return (
    <>
      <TextInput
        disabled={systemManaged}
        error={!!errors.networkName}
        fullWidth
        helperText="Required and network names cannot start with an underscore."
        label="Network name"
        onChange={(networkName: string | null) =>
          update({ networkName: networkName ?? "" })
        }
        required
        value={value.networkName || ""}
      />
      <Stack alignItems="start" direction="row" spacing={2}>
        <Autocomplete
          inputProps={{
            helperText:
              "You'll only be able to upgrade or downgrade players under this network to the specified version or lower.",
            label: "Restrict Player Version",
          }}
          onChange={(_e: unknown, opt: StringOption | null) =>
            update({ playerVersion: opt })
          }
          options={playerVersionOptions}
          size="small"
          value={value.playerVersion}
        />
        <Autocomplete
          inputProps={{
            helperText:
              "You'll only be able to upgrade agents under this network to the specified version or lower.",
            label: "Restrict Agent Version",
          }}
          onChange={(_e: unknown, opt: StringOption | null) =>
            update({ agentVersion: opt })
          }
          options={agentVersionOptions}
          size="small"
          value={value.agentVersion}
        />
      </Stack>
      <ToggleSection
        checked={value?.hasAutoRestartSchedule}
        checkedLabel="On"
        description="Players belong to this network will auto restart at the selected hour and day every week. At restart time only the Cortex player will restart. The operating system will not get rebooted. The actual restart time may vary slightly from what you have selected."
        onChange={() => {
          update({ hasAutoRestartSchedule: !value.hasAutoRestartSchedule });
        }}
        title="Auto restart schedule"
        uncheckedLabel="Off"
      >
        <Stack alignItems="start" direction="row" spacing={2}>
          <Autocomplete
            disableClearable
            inputProps={{
              label: "Day",
              sx: { backgroundColor: Colors.white },
            }}
            onChange={(_e: unknown, opt: NumberOption | null) => {
              if (opt) {
                update({ day: opt.value });
              }
            }}
            options={dayOptions}
            size="small"
            value={
              selectedDayOption ?? {
                label: String(value.day),
                value: value.day,
              }
            }
          />
          <Autocomplete
            disableClearable
            inputProps={{
              label: "Hour",
              sx: { backgroundColor: Colors.white },
            }}
            onChange={(_e: unknown, opt: NumberOption | null) => {
              if (opt) {
                update({ hour: opt.value });
              }
            }}
            options={hourOptions}
            size="small"
            value={
              selectedHourOption ?? {
                label: String(value.hour),
                value: value.hour,
              }
            }
          />
        </Stack>
      </ToggleSection>
      <ToggleSection
        checked={value?.sandboxStatus}
        checkedLabel="On"
        description="Sandbox networks are for testing / staging devices. Devices under a sandbox network will not be included in some reports / stats."
        onChange={() => update({ sandboxStatus: !value.sandboxStatus })}
        title="Sandbox network"
        uncheckedLabel="Off"
      ></ToggleSection>
      <ToggleSection
        checked={!value?.deviceUpdateDisabled}
        checkedLabel="On"
        description="When enabled, you can upgrade Cortex players and agents remotely, from Fleet. When disabled, you need to handle upgrades manually."
        onChange={() =>
          update({ deviceUpdateDisabled: !value.deviceUpdateDisabled })
        }
        title="Remote upgrades"
        uncheckedLabel="Off"
      ></ToggleSection>
      {!isStrategiesEnabled && (
        <ToggleSection
          checked={value?.hasSystemStrategy}
          checkedLabel="On"
          description="Choose the default application devices in this network play, and the fallback creative(s) they should play if the app provides none. You can override these defaults on individual devices."
          disabled={readonly || userIsOperator}
          onChange={() => {
            update({ hasSystemStrategy: !value.hasSystemStrategy });
          }}
          title="Content Source"
          uncheckedLabel="Off"
        >
          <SystemStrategyForm
            applicationOptions={applicationOptions}
            onChange={(nextValue) => {
              update({
                fallbackContents:
                  nextValue.fallbackContents === undefined
                    ? value.fallbackContents
                    : (nextValue.fallbackContents as FormData["fallbackContents"]),
                selectedApplication:
                  nextValue.selectedApplication === undefined
                    ? value.selectedApplication
                    : nextValue.selectedApplication,
              });
            }}
            readOnly={readonly || userIsOperator}
            type="network"
            value={{
              fallbackContents: value.fallbackContents as ComponentProps<
                typeof SystemStrategyForm
              >["value"]["fallbackContents"],
              selectedApplication: value.selectedApplication as ComponentProps<
                typeof SystemStrategyForm
              >["value"]["selectedApplication"],
            }}
          />
        </ToggleSection>
      )}
      <ToggleSection
        checked={true}
        checkedLabel="On"
        description={
          <>
            Players belong to this network will only operate between these hours
            and the display will be turned off outside of these hours.{" "}
            <strong>00:00 - 24:00 means it operates at all times.</strong>
          </>
        }
        disabled={true}
        onChange={() => {}}
        title="Display Operation Schedule"
        uncheckedLabel="Off"
      >
        <OperationScheduleForm
          onChange={(deviceOperateTime) => update({ deviceOperateTime })}
          readOnly={readonly}
          schedule={value.deviceOperateTime}
        />
      </ToggleSection>
    </>
  );
}
